import { useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import {
  buildRainViewerTileUrlTemplate,
  RAINVIEWER_ATTRIBUTION,
  RAINVIEWER_MAX_ZOOM,
  type RainViewerRadarFrame,
} from './rainViewerWeather'

function createRainViewerLayer(urlTemplate: string): L.TileLayer {
  // Force integer z — Control Center uses zoomSnap 0.1 and fractional z breaks RainViewer.
  const LayerClass = L.TileLayer.extend({
    getTileUrl(this: L.TileLayer, coords: L.Coords) {
      const z = Math.max(0, Math.min(RAINVIEWER_MAX_ZOOM, Math.round(Number(coords.z))))
      const floored = L.point(coords.x, coords.y) as L.Coords
      floored.z = z
      return L.TileLayer.prototype.getTileUrl.call(this, floored)
    },
  }) as unknown as { new (url: string, options?: L.TileLayerOptions): L.TileLayer }

  return new LayerClass(urlTemplate, {
    opacity: 0.6,
    maxNativeZoom: RAINVIEWER_MAX_ZOOM,
    maxZoom: 20,
    keepBuffer: 0,
    updateWhenZooming: false,
    updateWhenIdle: true,
    attribution: RAINVIEWER_ATTRIBUTION,
  })
}

/** Current-frame radar overlay (viewport tiles only). Playback/scrub is disabled for now. */
export function RainViewerRadarLayer({ frame }: { frame: RainViewerRadarFrame }) {
  const map = useMap()
  const layerRef = useRef<L.TileLayer | null>(null)

  useEffect(() => {
    const layer = createRainViewerLayer(buildRainViewerTileUrlTemplate(frame))
    layer.addTo(map)
    layerRef.current = layer
    return () => {
      map.removeLayer(layer)
      layerRef.current = null
    }
    // Mount once; frame path changes use setUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    const layer = layerRef.current
    if (!layer) return
    layer.setUrl(buildRainViewerTileUrlTemplate(frame))
  }, [frame])

  return null
}
